import {
  DialogContent,
  FormSection,
  MenuEmpty,
  MenuInput,
  MenuItems,
  ModalFooter,
  ModalHeader,
  NextAlert,
  NextButton,
  NextDialog,
  NextLabel,
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuProvider,
  SelectMenuTriggerType,
  Spinner,
  useToast,
} from '@cint/wonderment';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  RecruitmentMarketingConnectionProviderConfig,
  RecruitmentMarketingProvider,
} from '@/utils/types';
import { URLS } from '@/utils/urls';

import { LeaveConfirmationDialog } from './LeaveConfirmationDialog';
import {
  CONVERSION_EVENTS,
  ConversionEventId,
  RECRUITMENT_MARKETING_PROVIDER_DETAILS,
} from './recruitmentMarketingProviders';
import {
  useProviderReconnect,
  useRecruitmentMarketingAccounts,
  useSaveAccountSettings,
} from './useRecruitmentMarketingConnection';

// A short list is quicker to scan than to search.
const CONVERSION_ACTION_SEARCH_THRESHOLD = 5;

export interface ProviderConnectionModalProps {
  isOpen: boolean;
  provider: RecruitmentMarketingProvider;
  companyId: string;
  panelId: string;
  connectionId: string;
  /**
   * The mapping already saved for this connection, off the panel's embedded
   * connection. Seeds the fields so Settings opens on what is configured
   * rather than blank — absent for a connection that has never been set up.
   */
  savedConfig?: RecruitmentMarketingConnectionProviderConfig | null;
  /**
   * True while the panel data `savedConfig` is read from is still loading.
   * Seeding is held back until then — seeding happens once, so seeding from
   * the accounts alone would lock in a blank mapping before the saved one
   * arrives.
   */
  isSavedConfigLoading?: boolean;
  mode?: 'confirm' | 'settings';
  onClose: () => void;
  onSaved: () => void;
}

export const ProviderConnectionModal = ({
  isOpen,
  provider,
  companyId,
  panelId,
  connectionId,
  savedConfig,
  isSavedConfigLoading = false,
  mode = 'confirm',
  onClose,
  onSaved,
}: ProviderConnectionModalProps) => {
  const {
    label,
    shortName: providerName,
    accountLabel,
    modalDescription,
    settingsField,
  } = RECRUITMENT_MARKETING_PROVIDER_DETAILS[provider];
  const usesEventMapping = settingsField === 'events';
  const accountTitle = `${accountLabel
    .charAt(0)
    .toUpperCase()}${accountLabel.slice(1)}`;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const [selectedPixelId, setSelectedPixelId] = useState<string>();
  const [eventExternalIds, setEventExternalIds] = useState<
    Partial<Record<ConversionEventId, string>>
  >({});
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const hasAutoSavedRef = useRef(false);

  const { data, isError: isAccountsError } = useRecruitmentMarketingAccounts(
    connectionId,
    isOpen
  );
  const { mutate: saveSettings, isPending: isSaving } = useSaveAccountSettings(
    provider,
    connectionId
  );
  const { reconnect, isReconnecting } = useProviderReconnect(
    provider,
    companyId,
    panelId
  );

  const accounts = useMemo(() => data?.data ?? [], [data]);
  const selectedAccount = accounts.find(
    (account) => account.business_account_id === selectedAccountId
  );
  const pixelOptions = selectedAccount?.external_accounts ?? [];
  const selectedPixel = pixelOptions.find(
    (pixel) => pixel.external_id === selectedPixelId
  );
  // Event mapping is an explicit choice with no safe default, so those
  // providers always show the modal even when there's nothing to pick account-side.
  const shouldAutoSave =
    !usesEventMapping &&
    mode === 'confirm' &&
    accounts.length === 1 &&
    accounts[0].external_accounts.length === 1;
  // For Google, `external_accounts` is the pool of conversion actions rather
  // than pixels — the same payload field, a different thing to the user.
  const conversionActions = selectedAccount?.external_accounts ?? [];
  // Covers the gap between the accounts arriving and the seeding effect
  // picking one, so the fields never flash unselected.
  const isLoading =
    !data ||
    isSavedConfigLoading ||
    (accounts.length > 0 && !selectedAccountId);
  // Only a confirm-mode pixel provider can skip the modal, and that isn't known
  // until the accounts load — everything else opens straight onto the spinner.
  const mayAutoSave = !usesEventMapping && mode === 'confirm';
  const canSave =
    !!selectedAccountId &&
    (usesEventMapping
      ? CONVERSION_EVENTS.some((event) => !!eventExternalIds[event.id])
      : !!selectedPixelId);

  useEffect(() => {
    if (
      !isOpen ||
      isSavedConfigLoading ||
      accounts.length === 0 ||
      selectedAccountId
    )
      return;

    const savedGoogle = savedConfig?.google;
    const savedMeta = savedConfig?.meta;
    // Only trust a saved mapping whose account is still on offer — the pixel
    // and conversion action ids belong to that account and nowhere else.
    const savedAccount = accounts.find(
      (account) =>
        account.business_account_id ===
        (usesEventMapping ? savedGoogle : savedMeta)?.business_account_id
    );
    const account = savedAccount ?? accounts[0];
    const externalAccounts = account.external_accounts;

    setSelectedAccountId(account.business_account_id);

    if (usesEventMapping) {
      setSelectedPixelId(externalAccounts[0]?.external_id);

      if (savedAccount && savedGoogle) {
        setEventExternalIds(
          CONVERSION_EVENTS.reduce((seed, event) => {
            const externalId = savedGoogle[event.payloadKey];
            return externalId ? { ...seed, [event.id]: externalId } : seed;
          }, {})
        );
      }
      return;
    }

    const savedPixel = savedAccount
      ? externalAccounts.find(
          (pixel) => pixel.external_id === savedMeta?.external_id
        )
      : undefined;
    setSelectedPixelId((savedPixel ?? externalAccounts[0])?.external_id);
  }, [
    isOpen,
    isSavedConfigLoading,
    accounts,
    selectedAccountId,
    savedConfig,
    usesEventMapping,
  ]);

  // Clear on close so the next open re-seeds from freshly fetched panel data
  // rather than showing what was on screen last time.
  useEffect(() => {
    if (isOpen) return;
    hasAutoSavedRef.current = false;
    setSelectedAccountId(undefined);
    setSelectedPixelId(undefined);
    setEventExternalIds({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isAccountsError) return;

    toast({
      variant: 'error',
      description: `Failed to load ${providerName} accounts. Please try again.`,
    });
    setSelectedAccountId(undefined);
    setSelectedPixelId(undefined);
    setIsLeaveConfirmOpen(false);
    onClose();
  }, [isOpen, isAccountsError, providerName, onClose, toast]);

  const handleAccountChange = ([accountId]: string[]) => {
    setSelectedAccountId(accountId);
    const account = accounts.find(
      (acc) => acc.business_account_id === accountId
    );
    setSelectedPixelId(account?.external_accounts[0]?.external_id);
    setEventExternalIds({});
  };

  const handleConversionActionChange = (
    eventId: ConversionEventId,
    externalId: string
  ) =>
    setEventExternalIds((current) => ({ ...current, [eventId]: externalId }));

  const handleSave = () => {
    if (!canSave) return;
    saveSettings(
      usesEventMapping
        ? {
            provider_config: {
              google: CONVERSION_EVENTS.reduce(
                (config, event) => {
                  const externalId = eventExternalIds[event.id];
                  return externalId
                    ? { ...config, [event.payloadKey]: externalId }
                    : config;
                },
                { business_account_id: selectedAccountId }
              ),
            },
          }
        : {
            provider_config: {
              meta: {
                business_account_id: selectedAccountId,
                external_id: selectedPixelId,
              },
            },
          },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [URLS.getPanel(panelId)],
          });
          onSaved();
        },
      }
    );
  };

  const handleChangeAccount = () => reconnect(connectionId);

  useEffect(() => {
    if (
      isOpen &&
      shouldAutoSave &&
      selectedAccountId &&
      selectedPixelId &&
      !isSaving &&
      !hasAutoSavedRef.current
    ) {
      hasAutoSavedRef.current = true;
      handleSave();
    }
  });

  return (
    <>
      <NextDialog
        open={isOpen && !shouldAutoSave && !(mayAutoSave && isLoading)}
      >
        <DialogContent>
          <ModalHeader
            title={mode === 'settings' ? `${label} settings` : 'Almost there'}
            isDismissible={false}
            description={modalDescription}
          />
          {mode === 'settings' && (
            <NextAlert
              description="Changes will only impact new events - won't happen retrospectively."
              iconName="Info"
              variant="info"
            />
          )}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : (
            <FormSection size="base" variant="vertical">
              <div>
                <NextLabel label={accountTitle} size="sm" className="mb-2" />
                <SelectMenuProvider
                  isMultiSelect={false}
                  onSelectedChange={handleAccountChange}
                  selectedItems={selectedAccountId ? [selectedAccountId] : []}
                >
                  <SelectMenu
                    triggerProps={{
                      type: SelectMenuTriggerType.SelectButton,
                      props: {
                        label:
                          selectedAccount?.business_account_name ??
                          `Select a ${accountLabel}`,
                        variant: 'outline',
                        className: 'w-full !justify-between !text-left',
                        isDisabled: accounts.length <= 1,
                      },
                    }}
                  >
                    <SelectMenuContent>
                      <MenuItems>
                        {accounts.map((account) => (
                          <SelectMenuItem
                            key={account.business_account_id}
                            label={account.business_account_name}
                            value={account.business_account_id}
                          />
                        ))}
                      </MenuItems>
                    </SelectMenuContent>
                  </SelectMenu>
                </SelectMenuProvider>
              </div>
              {usesEventMapping ? (
                <div>
                  <NextLabel
                    label="Event mapping"
                    isRequired
                    size="sm"
                    className="mb-2"
                  />
                  <div className="flex flex-col gap-4">
                    {CONVERSION_EVENTS.map((event) => {
                      const selectedAction = conversionActions.find(
                        (action) =>
                          action.external_id === eventExternalIds[event.id]
                      );

                      return (
                        <div key={event.id} className="flex flex-col gap-2">
                          <NextLabel
                            label={event.label.toUpperCase()}
                            tooltipContent={event.tooltip}
                            size="sm"
                          />
                          <SelectMenuProvider
                            isMultiSelect={false}
                            onSelectedChange={([externalId]) =>
                              handleConversionActionChange(event.id, externalId)
                            }
                            selectedItems={
                              selectedAction ? [selectedAction.external_id] : []
                            }
                          >
                            <SelectMenu
                              triggerProps={{
                                type: SelectMenuTriggerType.SelectButton,
                                props: {
                                  label:
                                    selectedAction?.external_name ??
                                    'Select a conversion action',
                                  variant: 'outline',
                                  className:
                                    'w-full !justify-between !text-left',
                                  isDisabled:
                                    isSaving || conversionActions.length === 0,
                                },
                              }}
                            >
                              <SelectMenuContent>
                                {conversionActions.length >
                                  CONVERSION_ACTION_SEARCH_THRESHOLD && (
                                  <>
                                    <MenuInput placeholder="Select conversion action" />
                                    <MenuEmpty>
                                      No conversion actions found.
                                    </MenuEmpty>
                                  </>
                                )}
                                <MenuItems>
                                  {conversionActions.map((action) => (
                                    <SelectMenuItem
                                      key={action.external_id}
                                      label={action.external_name}
                                      value={action.external_id}
                                    />
                                  ))}
                                </MenuItems>
                              </SelectMenuContent>
                            </SelectMenu>
                          </SelectMenuProvider>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <NextLabel label="Pixel" size="sm" className="mb-2" />
                  <SelectMenuProvider
                    isMultiSelect={false}
                    onSelectedChange={([id]) => setSelectedPixelId(id)}
                    selectedItems={selectedPixelId ? [selectedPixelId] : []}
                  >
                    <SelectMenu
                      triggerProps={{
                        type: SelectMenuTriggerType.SelectButton,
                        props: {
                          label:
                            selectedPixel?.external_name ?? 'Select a pixel',
                          variant: 'outline',
                          className: 'w-full !justify-between !text-left',
                          isDisabled: pixelOptions.length <= 1,
                        },
                      }}
                    >
                      <SelectMenuContent>
                        <MenuItems>
                          {pixelOptions.map((pixel) => (
                            <SelectMenuItem
                              key={pixel.external_id}
                              label={pixel.external_name}
                              value={pixel.external_id}
                            />
                          ))}
                        </MenuItems>
                      </SelectMenuContent>
                    </SelectMenu>
                  </SelectMenuProvider>
                </div>
              )}
            </FormSection>
          )}
          <ModalFooter
            primaryButtonProps={{
              text: 'Save settings',
              onClick: handleSave,
              isDisabled: !canSave || isSaving,
            }}
            secondaryButtonProps={{
              text: 'Cancel',
              onClick: () => setIsLeaveConfirmOpen(true),
              isDisabled: isSaving,
            }}
            tertiaryAction={
              <NextButton
                variant="link"
                size="base"
                text="Change account"
                onClick={handleChangeAccount}
                isDisabled={isSaving || isReconnecting}
                isLoading={isReconnecting}
                className="!px-0"
              />
            }
          />
        </DialogContent>
      </NextDialog>
      <LeaveConfirmationDialog
        isOpen={isLeaveConfirmOpen}
        providerName={providerName}
        onStay={() => setIsLeaveConfirmOpen(false)}
        onLeave={() => {
          setIsLeaveConfirmOpen(false);
          onClose();
        }}
      />
    </>
  );
};
